import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import axiosInstance, { getUserDetails, canEditSamplingFishData,
  handleLegacySessionExpired
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import CustomModal from "../../components/custom/customModal";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import { isWrAboveFlagThreshold } from "../../utils/pondSamplingUpload";
import { confirmHighWrSamples } from "../../utils/wrFlagConfirm";


const UpdateSamplingDetail = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const userDetails = getUserDetails();
  const canSaveSampling = canEditSamplingFishData(userDetails);
  const FisherID = userDetails.FisherID;
  const history = useHistory();

  // Data state
  const [rows, setRows] = useState([]);
  const [availableFishes, setAvailableFishes] = useState([]);
  const [pond, setPond] = useState({});
  const [hasExistingData, setHasExistingData] = useState(false);

  // Image handling state
  const [preview, setPreview] = useState(null);
  const [imageToShow, setImageToShow] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedID, setSelectedID] = useState(null);

  // Fetch all required data
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      // 1. Fetch pond details
      const pondResponse = await axiosInstance.get(`/api/Pond/GetPondByPondDetailID`, {
        params: { id: id, FisherID: FisherID }
      });
      setPond(pondResponse.data[0]);

      // 2. Fetch available fish types for this pond
      const fishResponse = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${id}`);
      setAvailableFishes(fishResponse.data);

      // 3. Check for existing sampling data
      try {
        const samplingResponse = await axiosInstance.get(`/api/FishSampling/GetAllSamplesViaPondDetail/${id}`);
        if (samplingResponse.data && samplingResponse.data.length > 0) {
          setHasExistingData(true);
          initializeExistingRows(samplingResponse.data, fishResponse.data);
        } else {
          setHasExistingData(false);
          initializeNewRows(fishResponse.data);
        }
      } catch (error) {

        if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      }
        setHasExistingData(false);
        initializeNewRows(fishResponse.data);
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch required data.";
        console.error("Failed to fetch required data", error);
        showErrorToast(errorMessage);
      }
    }
  }, [id, FisherID]);

  // Initialize rows for existing data
  const initializeExistingRows = (samplingData, fishData) => {
    // Create a map of fish details for equation lookup
    const fishMap = {};
    fishData.forEach(fish => {
      fishMap[fish.FishTypeID] = {
        wsEquation: pond.LengthUnitName === "MM" ? fish.WsMGEquation : fish.WsIPEquation,
        fishTypeName: fish.FishTypeName
      };
    });

    const initializedRows = samplingData.map(sample => ({
      id: sample.FishSamplingID,
      fishTypeId: sample.FishTypeID,
      fishTypeName: sample.FishTypeName,
      length: sample.Length,
      weight: sample.Weight,
      ws: sample.Ws,
      wr: sample.Wr,
      tagNumber: sample.TagNumber || '',
      status: sample.Status,
      equation: fishMap[sample.FishTypeID]?.wsEquation || "",
      image: sample.Image
    }));

    setRows(initializedRows);
  };

  // Initialize rows for new data
  const initializeNewRows = (fishData) => {
    // Start with one empty row
    setRows([{
      fishTypeId: "",
      length: "",
      weight: "",
      ws: "",
      wr: "",
      tagNumber: "",
      status: "Culled",
      equation: ""
    }]);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Image handling functions
  const openImageModal = (id) => {
    setSelectedID(id);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setPreview(null);
    setSelectedImage(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        showErrorToast("Image size should be less than 4MB");
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const showImagePreview = (blobData) => {
    setModalVisible(true);
    const imageData = blobData.data;
    const base64Image = `data:image/jpeg;base64,${Buffer.from(imageData).toString("base64")}`;
    setImageToShow(base64Image);
  };

  const handleImageUpload = async () => {
    if (!selectedImage) {
      showErrorToast("Please select an image first");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('image', selectedImage);

    try {
      await axiosInstance.post(`/api/FishSampling/AddImageToSample/${selectedID}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showSuccessToast('Image uploaded successfully');
      fetchData(); // Refresh data
    } catch (error) {
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to upload image";
        console.error("Failed to upload image", error);
        showErrorToast(errorMessage);
      }
    } finally {
      closeImageModal();
      setIsLoading(false);
    }
  };

  // Row manipulation functions
  const addRow = () => {
    setRows([...rows, {
      fishTypeId: "",
      length: "",
      weight: "",
      ws: "",
      wr: "",
      status: "Culled",
      equation: ""
    }]);
  };

  const removeRow = (index) => {
    if (rows.length <= 1) {
      showErrorToast("At least one row is required");
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleFishTypeChange = (index, fishTypeId) => {
    const selectedFish = availableFishes.find(fish => fish.FishTypeID === fishTypeId);
    const equation = pond.LengthUnitName === "MM"
      ? selectedFish?.WsMGEquation
      : selectedFish?.WsIPEquation;

    const updatedRows = [...rows];
    updatedRows[index] = {
      ...updatedRows[index],
      fishTypeId,
      fishTypeName: selectedFish?.FishTypeName || "",
      equation: equation || "",
      length: "",
      weight: "",
      ws: "",
      wr: ""
    };

    setRows(updatedRows);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;

    // Recalculate if length or weight changes
    if (field === "length" || field === "weight") {
      calculateWsWr(updatedRows, index);
    }

    setRows(updatedRows);
  };

  const calculateWsWr = (rowsArray, index) => {
    const row = rowsArray[index];

    if (!row.equation || !row.length) {
      row.ws = "";
      row.wr = "";
      return;
    }

    try {
      // Replace "L" with the actual length and fix exponentiation
      const replacedExpression = row.equation.replace(/L/g, row.length).replace(/\^/g, '**');
      const calculatedWs = Function(`return ${replacedExpression}`)();

      if (calculatedWs > 0) {
        row.ws = calculatedWs.toFixed(2);

        // Calculate Wr if weight is available
        if (row.weight) {
          row.wr = (row.weight / calculatedWs).toFixed(2);
        } else {
          row.wr = "";
        }
      } else {
        row.ws = "";
        row.wr = "";
      }
    } catch (error) {
      console.error("Error calculating Ws:", error);
      row.ws = "";
      row.wr = "";
    }
  };

  const formatWrAsPercent = (wr) => {
    if (wr === "" || wr === undefined || wr === null) return "";
    const num = parseFloat(wr);
    if (Number.isNaN(num)) return "";
    return (num * 100).toFixed(1) + "%";
  };

  // Data validation
  const validateData = () => {
    // Check if at least one row has all required fields
    const hasValidRow = rows.some(row =>
      row.fishTypeId && row.length > 0 && row.weight > 0
    );

    if (!hasValidRow) {
      showErrorToast("Please provide at least one complete sample with fish type, length and weight");
      return false;
    }

    // Check all rows with data are complete
    const incompleteRows = rows.filter(row =>
      (row.fishTypeId || row.length || row.weight) &&
      (!row.fishTypeId || !row.length || !row.weight)
    );

    if (incompleteRows.length > 0) {
      showErrorToast("Some rows have incomplete data. Please fill all fields or remove them.");
      return false;
    }

    return true;
  };

  // Save data (handles both insert and update)
  const saveData = async () => {
    if (!validateData()) return;

    const completeRows = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.fishTypeId && row.length > 0 && row.weight > 0);

    const flaggedMeta = completeRows
      .filter(({ row }) => isWrAboveFlagThreshold(row.wr))
      .map(({ row, index }) => ({
        index: index + 1,
        species: row.fishTypeName || "—",
        length: row.length,
        weight: row.weight,
        wr: Number(row.wr),
      }));

    let rowsToSave = completeRows.map(({ row }) => row);
    if (flaggedMeta.length > 0) {
      const decision = await confirmHighWrSamples(flaggedMeta, {
        actionVerb: "saving",
        allowExcludeFlagged: completeRows.length > 1,
        confirmAllText:
          completeRows.length > 1
            ? "Save all including flagged"
            : "Save anyway",
        excludeFlaggedText: "Save only OK rows",
      });
      if (decision === "cancel") return;
      if (decision === "exclude_flagged") {
        rowsToSave = completeRows
          .filter(({ row }) => !isWrAboveFlagThreshold(row.wr))
          .map(({ row }) => row);
        if (rowsToSave.length === 0) {
          showErrorToast("No non-flagged rows to save");
          return;
        }
      }
    }

    // Prepare payload - we'll send all rows with complete data
    const payload = rowsToSave.map((row) => ({
      ...(hasExistingData && row.id && { id: row.id }), // Include ID for existing records
      FishTypeID: row.fishTypeId,
      PondDID: id,
      Length: parseFloat(row.length),
      Weight: parseFloat(row.weight),
      Wr: parseFloat(row.wr || 0),
      Ws: parseFloat(row.ws || 0),
      TagNumber: row.tagNumber || "",
      Status: row.status,
      FisherID: FisherID,
    }));

    setIsLoading(true);
    try {
      // Using POST for both cases as per your requirement
      await axiosInstance.post("/api/FishSampling/SaveSamples", {
        pondDetailId: id,
        samples: payload
      });

      showSuccessToast("Sampling data saved successfully");
      history.push("/samplingViaPondDetail/" + id);
    } catch (error) {

      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to save sampling data.";
        console.error("Failed to save sampling data.", error);
        showErrorToast(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    isLoading ? <Preloader /> :
      <div>
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
          <div className="mb-3 mb-md-0 flex-grow-1" style={{ minWidth: 0 }}>
            <PondDetailWorkflowPageHeader
              pondDetailId={id}
              section="sampling"
              detailName={pond?.Name}
              noBottomMargin
            />
          </div>
          <div className="ms-md-auto">
            {canSaveSampling && (
              <button
                type="button"
                onClick={saveData}
                className="btn btn-success btn-rounded px-4 rounded-pill"
              >
                <i className="fa fa-save fa-lg me-2 ms-n2 text-success-900"></i>
                Save
              </button>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="card border-0 shadow-sm mb-4 donezo-workflow-tabs">
          <ul className="nav nav-tabs border-0 px-3 pt-3" style={{ borderBottom: '2px solid #e9ecef' }}>
            <li className="nav-item me-2">
              <Link
                to={"/UpdatePD/" + id}
                className="nav-link px-3 py-2 rounded-top"
                style={{
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                <i className="fas fa-cog me-2" aria-hidden />General
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={"/pondFishesViaPondDetail/" + id}
                className="nav-link px-3 py-2 rounded-top"
                style={{
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                <i className="fas fa-fish me-2" aria-hidden />Pond Detail Fishes
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={"/samplingViaPondDetail/" + id}
                className="nav-link px-3 py-2 rounded-top"
                style={{
                  color: '#348fe2',
                  backgroundColor: '#ffffff',
                  border: '2px solid #348fe2',
                  borderBottom: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                <i className="fas fa-chart-line me-2" aria-hidden />Sampling Detail
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={"/FishDataTableView/" + id}
                className="nav-link px-3 py-2 rounded-top"
                style={{
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                }}
              >
                <i className="fas fa-table me-2" aria-hidden />Table view
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={"/fish-data-graph-new/" + id}
                className="nav-link px-3 py-2 rounded-top"
                style={{
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                }}
              >
                <i className="fas fa-chart-area me-2" aria-hidden />Graph view
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={"/pondNoteViaPondDetail/" + id}
                className="nav-link px-3 py-2 rounded-top"
                style={{
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                <i className="fas fa-note-sticky me-2" aria-hidden />Notes
              </Link>
            </li>
          </ul>
        </div>

        {/* Tab Content */}
        <div className="card border-0 shadow-sm donezo-workflow-card">
          <div className="tab-content p-3 p-md-4">
            <div className="tab-pane fade show active" id="allTab">
              <div className="table-responsive mb-3">
                <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Fish Type</th>
                      <th>Length ({pond.LengthUnitName || ""})</th>
                      <th>Weight (g)</th>
                      <th>Equation</th>
                      <th>Ws</th>
                      <th>Wr (%)</th>
                      <th>Tag Number</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={index}>
                        <td>
                          {hasExistingData ? (
                            <input
                              type="text"
                              className="form-control"
                              value={row.fishTypeName}
                              readOnly
                            />
                          ) : (
                            <select
                              className="form-control"
                              value={row.fishTypeId}
                              onChange={(e) => handleFishTypeChange(index, e.target.value)}
                              required
                            >
                              <option value="">Select Fish</option>
                              {availableFishes.map(fish => (
                                <option key={fish.FishTypeID} value={fish.FishTypeID}>
                                  {fish.FishTypeName}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={row.length}
                            onChange={(e) => handleInputChange(index, "length", e.target.value)}
                            min="0"
                            step="0.1"
                            required
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={row.weight}
                            onChange={(e) => handleInputChange(index, "weight", e.target.value)}
                            min="0"
                            step="0.1"
                            required
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={row.equation}
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={row.ws}
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={formatWrAsPercent(row.wr)}
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={row.tagNumber}
                            onChange={(e) => handleInputChange(index, "tagNumber", e.target.value)}
                            placeholder="Tag Number"
                          />
                        </td>
                        <td>
                          <select
                            className="form-control"
                            value={row.status}
                            onChange={(e) => handleInputChange(index, "status", e.target.value)}
                          >
                            <option value="Culled">Culled</option>
                            <option value="Released">Released</option>
                          </select>
                        </td>
                        <td>
                          {hasExistingData && row.image && (
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => showImagePreview(row.image)}
                              title="View image"
                            >

                            </button>
                          )}
                          {hasExistingData && (
                            <button
                              className="btn btn-sm btn-outline-secondary me-2"
                              onClick={() => openImageModal(row.id)}
                              title="Upload image"
                            >

                            </button>
                          )}
                          {(!hasExistingData || rows.length > 1) && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeRow(index)}
                              title="Remove row"
                            >

                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-end mb-3">
                <button
                  className="btn btn-primary"
                  onClick={addRow}
                >

                  Add Row
                </button>
              </div>
            </div>
          </div>

        </div>

        {/* Image Preview Modal */}
        <CustomModal
          isOpen={modalVisible}
          onClose={() => setModalVisible(false)}
          title="Fish Image"
          showButton={false}
          modalSize="modal-lg"
          minHeight="600px"
        >
          <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
          }}>
            <img
              src={imageToShow}
              alt="Fish"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </div>
        </CustomModal>

        {/* Image Upload Modal */}
        <CustomModal
          isOpen={isModalOpen}
          onClose={closeImageModal}
          title="Upload Fish Image"
          showButton={false}
          modalSize="modal-lg"
          minHeight="auto"
        >
          <span className="text-danger">(Size Should be Lower than 4MB)</span>
          <div className="mt-3">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="form-control"
            />
            {preview && (
              <div className="row pt-3">
                <img
                  src={preview}
                  alt="Selected Preview"
                  style={{
                    width: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </div>

          <div className="row pt-4">
            <div className="col-md-12 text-center">
              <button
                onClick={handleImageUpload}
                className="btn btn-success me-2"
                disabled={!selectedImage}
              >
                Upload
              </button>
              <button
                onClick={closeImageModal}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </CustomModal>
      </div>
  );
};

export default UpdateSamplingDetail;